
import { Component, Vue, Watch } from 'vue-property-decorator';
import { BugModel } from '@/models/bug';
import { BugList } from '@/collections/bugs';
import { Route } from 'vue-router';

const VALID_STATUSES = ['OPEN', 'CLOSED'];
const VALID_BUG_TYPES = ['EXTRACT', 'TRANSFORM', 'VIEW_REPORT_ERROR'];
const VALID_SORT_FIELDS = ['id', 'relatedIdsCount', 'code', 'status', 'bugType', 'createdAt', 'updatedAt'];
const VALID_ORDERS = ['asc', 'desc'];

const DEFAULT_SORT_FIELD = 'id';
const DEFAULT_ORDER = 'desc';
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 50;

interface BugOptions {
  filter: {
    status: string;
    bugType: string;
  };
  sort: {
    field: string;
    order: string;
  };
}

@Component({
  components: {
  },
})
export default class Bugs extends Vue {
  public isLoading: boolean = true;
  public currentPage: number = 1;
  public perPage: number = 50;
  public lastPage: number = 1;
  public bugs: BugList = new BugList();
  public options: BugOptions = {
    filter: {
      status: '',
      bugType: '',
    },
    sort: {
      field: 'id',
      order: 'desc',
    },
  };

  public created() {
    this.readUrlParams();
    this.isLoading = true;
    this.fetchData();
    this.isLoading = false;
  }

  @Watch('$route.query')
  public onRouteQueryChange(newQuery: Route['query'], oldQuery: Route['query']) {
    if (JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
      this.readUrlParams();
      this.fetchData();
    }
  }

  private readUrlParams() {
    const query = this.$route.query;

    this.currentPage = Number(query.page) || DEFAULT_PAGE;
    this.perPage = Number(query.perPage) || DEFAULT_PER_PAGE;

    const status = query.status as string;
    this.options.filter.status = VALID_STATUSES.includes(status) ? status : '';

    const bugType = query.bugType as string;
    this.options.filter.bugType = VALID_BUG_TYPES.includes(bugType) ? bugType : '';

    const orderBy = query.orderBy as string;
    this.options.sort.field = VALID_SORT_FIELDS.includes(orderBy) ? orderBy : DEFAULT_SORT_FIELD;

    const order = query.order as string;
    this.options.sort.order = VALID_ORDERS.includes(order) ? order : DEFAULT_ORDER;
  }

  private updateUrlParams() {
    const query: Record<string, string | undefined> = {};

    if (this.options.filter.status) {
      query.status = this.options.filter.status;
    }
    if (this.options.filter.bugType) {
      query.bugType = this.options.filter.bugType;
    }
    if (this.options.sort.field !== DEFAULT_SORT_FIELD) {
      query.orderBy = this.options.sort.field;
    }
    if (this.options.sort.order !== DEFAULT_ORDER) {
      query.order = this.options.sort.order;
    }
    if (this.currentPage !== DEFAULT_PAGE) {
      query.page = String(this.currentPage);
    }
    if (this.perPage !== DEFAULT_PER_PAGE) {
      query.perPage = String(this.perPage);
    }

    const currentQuery = this.$route.query;
    const isSameQuery = JSON.stringify(query) === JSON.stringify(currentQuery);

    if (!isSameQuery) {
      this.$router.replace({ query: query as any }).catch(() => {});
    }
  }

  public async fetchData() {
    this.isLoading = true;
    this.updateUrlParams();
    this.bugs = new BugList();
    await this.bugs.fetch(
      this.currentPage,
      this.perPage,
      {
        status: this.options.filter.status,
        bugType: this.options.filter.bugType,
        orderBy: this.options.sort.field,
        order: this.options.sort.order,
      },
    );
    this.lastPage = this.bugs.lastPage;
    this.isLoading = false;
  }

  public async clearOptions() {
    this.options = {
      filter: {
        status: '',
        bugType: '',
      },
      sort: {
        field: DEFAULT_SORT_FIELD,
        order: DEFAULT_ORDER,
      },
    };
    this.currentPage = DEFAULT_PAGE;
    await this.fetchData();
  }
}
